import * as Api from '@ViewModels';
import { useMutation, UseQueryResult } from '@tanstack/react-query';
import React from 'react';
import { IContentCalendarSelectedSuggestion } from '../../../../../../models';
import { useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { getUniqueIdForSuggestion } from '../../../../../../models/UiUtils';
import { pollSystemJob } from './systemJobPolling';

type GenerateBlogParams = {
	template: Api.ITemplate;
	suggestion: IContentCalendarSelectedSuggestion;
	signal: AbortSignal;
};

/**
 * Whether a blog template still needs generating — the single source of truth shared by the
 * generation effect and `awaitingGenerationIds`. A finished template (draft, or an AI-calendar blog
 * whose server-side generation completed) arrives with content and no prompt; a prompt means work is
 * still owed. Do NOT gate on `suggestion.calendarId` — AI-calendar suggestions always carry one, so
 * it skipped still-generating AI blogs.
 */
function isBlogAutoGenerationTarget(
	template: Api.IBlogTemplate | undefined,
	suggestion: IContentCalendarSelectedSuggestion | undefined
): boolean {
	if (!template || !suggestion) {
		return false;
	}
	return Boolean(template.prompt?.document);
}

/**
 * Automatically generates blog posts from templates when entering the multi-scheduler.
 * Each blog is generated via the blog/generateFromTemplate API, with per-item loading states.
 * This prevents raw prompts from being displayed to users.
 *
 * Skips generation for:
 * - Drafts (templates without a prompt — content is already written)
 * - Suggestions already on the calendar
 */
export function useAutoGenerateBlogPosts({
	templateQueries,
	selectedSuggestions,
	impersonationContext,
}: {
	templateQueries: UseQueryResult<Api.ITemplate, unknown>[];
	selectedSuggestions: IContentCalendarSelectedSuggestion[];
	impersonationContext?: Api.IImpersonationContext;
}) {
	const userSession = useUserSession();
	const [generatedPosts, setGeneratedPosts] = React.useState<Record<string, Api.IBlogPost>>({});
	const [errorIds, setErrorIds] = React.useState<string[]>([]);
	const startedIdsRef = React.useRef<Set<string>>(new Set());
	const abortControllerRef = React.useRef<AbortController>(null);
	const generatingIdsRef = React.useRef<Set<string>>(new Set());
	const [generatingIds, setGeneratingIds] = React.useState<string[]>([]);

	const generateMutation = useMutation({
		mutationFn: async ({ template, signal }: GenerateBlogParams) => {
			const systemJob = await userSession.webServiceHelper.callAsync<Api.IBlogGenerationSystemJob>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: 'blog/generateFromTemplate',
				}),
				'POST',
				template
			);

			const completedJob = systemJob.generatedContent
				? systemJob
				: await pollSystemJob(userSession, systemJob.id, signal);

			if (!completedJob?.generatedContent?.content) {
				throw new Error('Blog generation returned no content');
			}

			const post = completedJob.generatedContent.content;
			post.mainImage = template.attachments?.[0] as Api.IFileAttachmentWithURL;
			return post;
		},
	});

	const triggerGeneration = React.useCallback(
		(template: Api.ITemplate, suggestion: IContentCalendarSelectedSuggestion, signal: AbortSignal) => {
			const id = getUniqueIdForSuggestion(suggestion);
			generatingIdsRef.current.add(id);
			setGeneratingIds(Array.from(generatingIdsRef.current));

			generateMutation
				.mutateAsync({ template, suggestion, signal })
				.then(
					post => {
						if (!signal.aborted) {
							setGeneratedPosts(prev => ({ ...prev, [id]: post }));
						}
					},
					() => {
						if (!signal.aborted) {
							setErrorIds(prev => [...prev, id]);
						}
					}
				)
				.finally(() => {
					generatingIdsRef.current.delete(id);
					if (!signal.aborted) {
						setGeneratingIds(Array.from(generatingIdsRef.current));
					}
				});
		},
		[generateMutation]
	);

	// Keep the latest triggerGeneration in a ref so the effect can read it without
	// re-subscribing when the callback identity changes (which would re-trigger the effect
	// and defeat the `startedIdsRef` dedupe).
	const triggerGenerationRef = React.useRef(triggerGeneration);
	triggerGenerationRef.current = triggerGeneration;

	// Trigger generation for each template as it becomes available.
	const templateIds = templateQueries
		.map(q => q.data?.id)
		.filter(Boolean)
		.join(',');

	// Create during render and abort only on unmount; recreating in a keyed effect aborted in-flight generations.
	if (!abortControllerRef.current) {
		abortControllerRef.current = new AbortController();
	}
	React.useEffect(() => {
		return () => {
			abortControllerRef.current?.abort();
		};
	}, []);

	React.useEffect(() => {
		const { signal } = abortControllerRef.current;

		templateQueries.forEach((query, i) => {
			if (!query.data) return;
			const suggestion = selectedSuggestions[i];
			if (!suggestion) return;
			const id = getUniqueIdForSuggestion(suggestion);

			if (startedIdsRef.current.has(id)) return;
			startedIdsRef.current.add(id);

			if (isBlogAutoGenerationTarget(query.data as Api.IBlogTemplate, suggestion)) {
				triggerGenerationRef.current(query.data, suggestion, signal);
			}
		});
		// Generations are NOT aborted here — only on unmount (effect above) — so a later template
		// load can't cancel an in-flight generation. Keyed off the stable `templateIds` string so it
		// only fires when the set of loaded templates changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [templateIds]);

	const retryGeneration = React.useCallback(
		(suggestion: IContentCalendarSelectedSuggestion) => {
			const id = getUniqueIdForSuggestion(suggestion);
			// Guard against concurrent retries — if a generation for this id is still in flight,
			// a second call would race and produce duplicate entries in `generatingIds`.
			if (generatingIdsRef.current.has(id)) return;

			const index = selectedSuggestions.findIndex(s => getUniqueIdForSuggestion(s) === id);
			const query = templateQueries[index];
			if (!query?.data) return;

			setErrorIds(prev => prev.filter(eid => eid !== id));
			startedIdsRef.current.add(id);

			const controller = abortControllerRef.current ?? new AbortController();
			triggerGeneration(query.data, suggestion, controller.signal);
		},
		[templateQueries, selectedSuggestions, triggerGeneration]
	);

	// Blogs still showing a spinner; the scheduler reads this instead of re-deriving the skip rules, so the two can't drift.
	const awaitingGenerationIds = React.useMemo(() => {
		const ids: string[] = [];
		templateQueries.forEach((query, i) => {
			const suggestion = selectedSuggestions[i];
			if (!isBlogAutoGenerationTarget(query.data as Api.IBlogTemplate | undefined, suggestion)) {
				return;
			}
			const id = getUniqueIdForSuggestion(suggestion);
			if (generatedPosts[id] || errorIds.includes(id)) {
				return;
			}
			ids.push(id);
		});
		return ids;
	}, [templateQueries, selectedSuggestions, generatedPosts, errorIds]);

	return { generatedPosts, generatingIds, errorIds, retryGeneration, awaitingGenerationIds };
}
